/**
 * A recipe's LOOK (band designs v68, ref-1 photo match): the light, tone and
 * floor a catalogue photograph was taken under, as a named host map over the
 * viewer's EXISTING scene RPCs — no contract surface of its own.
 *
 * "studio" is the Ring Builder's boot look (`webgl-jewelry-viewer/src/entries/
 * bandStudio.ts`): the measured studio EXR at 0.61, ACES, contrast 1.1, SSAO
 * 0.25, no floor mirror, a white page. It is what every recipe without a look
 * lands on, so refs 2–9 render exactly as before v68.
 *
 * "catalogue-white" is the white-background light-tent catalogue shot: the
 * viewer's generated `/env/light-tent.hdr` (v68 asset), a LINEAR tone curve
 * with a saturation grade (ACES's toe crushes blue out of the darks and turns
 * gold amber; the photo's shadow ratios are the albedo through a linear toe),
 * a faint mirror floor and a faint baked shadow, on #fefefe.
 *
 * The gem shader and the diamond environment are never touched here.
 *
 * Restoring "studio" puts back what the viewer held before the first
 * non-studio look was applied, read with the viewer's own getters at that
 * moment (`captureStudio`); a page that never showed the studio (a shared
 * link opened straight onto a catalogue look) falls back to the bandStudio
 * constants mirrored below.
 */
import type { GroundShadowBakePatch, PostFXSettingsPatch, RingBuilderClientApi, TonemapMode } from "../../../shared/api-contract";

export type RecipeLookId = "studio" | "catalogue-white";
export const RECIPE_LOOK_IDS: readonly RecipeLookId[] = ["studio", "catalogue-white"];
export const isRecipeLookId = (v: unknown): v is RecipeLookId => v === "studio" || v === "catalogue-white";

export interface RecipeLook {
  env: { url?: string; presetId?: string; intensity: number; rotationY: number };
  post: {
    tonemap: TonemapMode; contrast: number; saturation: number; ssaoIntensity: number; tonemapBackground: boolean; tonemapGround: boolean;
    /** The look's own colour grade, loaded into LUT slot `LOOK_LUT_SLOT` (critic round 1). Absent ⇒ no
     *  look LUT: the slot is cleared if a look had loaded one, and the LUT flags below are restored. */
    lut?: { url: string; intensity: number };
    /** The studio's LUT switch as captured (`getPostFX().lut`), put back when the studio returns.
     *  `lutGround` (v68, critic round 3) is captured too: the look turns it off. */
    lutFlags?: { enabled: boolean; intensity: number; lutBackground: boolean; lutGround?: boolean };
  };
  /** The ground shadow is drawn at `shadowOpacity` (`setDepthShadowOpacity`). Critic round 2: a look may
   *  also tint the mirror (`tint`, the ground material colour via `setGroundConfig`) and shape the bake
   *  (`bake`: softness / falloff, plus the sample count and blur `setGroundConfig` resets); the studio's
   *  own values of all of these are captured before the first look and written back with it, so a
   *  round trip still leaves the floor exactly as it was. Absent ⇒ not touched. */
  ground: { reflection: boolean; opacity: number; blur: number; shadowOpacity: number; tint?: string; bake?: GroundShadowBakePatch };
  background: string;
}

/**
 * The studio, mirrored from `entries/bandStudio.ts` (env id "bandStudio" ×
 * `BAND_STUDIO_ENV_INTENSITY` 0.61 at rotation 0; `post.grade` 1.1 / 1.0;
 * `post.ssao.intensity` 0.25; `tonemapBackground/Ground` false; background
 * #ffffff) and from the viewer's boot ground (reflection off; the bake's
 * default brightness / softness). Used only when no capture exists.
 */
const STUDIO_FALLBACK: RecipeLook = {
  env: { presetId: "bandStudio", intensity: 0.61, rotationY: 0 },
  post: { tonemap: "ACES_FILMIC", contrast: 1.1, saturation: 1.0, ssaoIntensity: 0.25, tonemapBackground: false, tonemapGround: false },
  // Read off a fresh /dev/ring (2026-10-31): reflection off at 0.85 / 0.5,
  // the baked shadow drawn at 0.55.
  ground: { reflection: false, opacity: 0.85, blur: 0.5, shadowOpacity: 0.55,
    // the boot ground material and bake, same read (`getGroundConfig().material`, `getGroundShadowBake()`)
    tint: "#e9e9e9", bake: { softness: 0.5, falloff: 1.25, sampleCount: 160, blurRadius: 2 } },
  background: "#ffffff",
};

/**
 * The catalogue light tent. Every number is tuned by the w4 ref-1 meters
 * (photo L/C/b/g of the metal faces, interiors, rims, floor), never by eye.
 */
const CATALOGUE_WHITE: RecipeLook = {
  // The viewer's measured light-tent (v68 asset; its table is the light the
  // ref-1 photo's gold mirrors, direction by direction) at its own scale.
  env: { url: "/env/light-tent.hdr", intensity: 1.0, rotationY: 0 },
  // LINEAR keeps the albedo's shadow ratios (b/g 0.74 against the photo's
  // 0.70; ACES crushed it to 0.33); 0.75 saturation brings the lit gold to the
  // photo's chroma (partner face ΔE76 2.8).
  // The grade (critic round 1): `/luts/catalogue-white.cube`, the viewer's generated cube
  // (`scripts/gen-catalogue-lut.mjs`) — keeps every colour's L*, lifts the gold's chroma in the
  // shadows, turns it a touch greener, and takes it to WHITE in the top few L* the way the photo's
  // polish goes white. Measured over the metal, C* and hue by tone (shadows / mid / highlights):
  // photo 21.6 / 82.2°, 23.9 / 86.0°, 17.8 / 89.1°; without it 18.8 / 80.3°, 23.0 / 83.0°,
  // 21.3 / 99.7°; with it 21.1 / 82.9°, 24.0 / 85.9°, 14.9 / 89.4°. Neutrals map to themselves, and
  // the background is never graded (`lutBackground` false).
  // SSAO 0.4 (was 0.1; critic round 2): the photo's metal is far less flat (L* sd 18 against our 11, 5 %
  // under L* 40 against 1 %); the occlusion darkens the bores, the seats and the grooves' floors, which
  // took A's face contrast to the photo's (p95−p5 46 against 46) with the rims still bright.
  // Saturation 0.79 (was 0.75; critic round 2): the gold's chroma p90 read 25.3 against the photo's
  // 28.4 and the partner paler; at 0.79 the partner's face ΔE76 stays ~2 and its shadows' C* ≤ 26.
  post: { tonemap: "LINEAR", contrast: 1.0, saturation: 0.79, ssaoIntensity: 0.4, tonemapBackground: false, tonemapGround: false,
    lut: { url: "/luts/catalogue-white.cube", intensity: 1 } },
  // A faint soft mirror and a contact shadow drawn darker than the studio's
  // (the bake itself untouched): the photo's floor reads L* 48–59 right under
  // each contact and ≥ 97 a ring's width away. At 0.25 / 0.8 the mirror read as
  // a second golden ring under each band (contacts L* 61 / 62); 0.12 / 1.0 with
  // the shadow at 0.85 gives 50 / 53 and a faint, short ghost like the photo's.
  // Critic round 1 (w4 ref-1): that shadow drew grey contact blobs (floor L* 54
  // under the rings against the photo's 81) and a grey haze between and behind
  // them (L* 93 against 99.7), and the mirror was too faint to show the photo's
  // ghost of each ring. The baked shadow is drawn at 0.15 and the mirror at
  // 0.5 / 0.5: the floor is white to the frame edge, the contacts read L* ~72
  // and each ring leaves a soft reflection.
  // Critic round 2: that mirror read as a crisp cream copy of each ring (edge gradient p99 2.5 against
  // the photo's 0.8, b* +3.3 against +0.7) with no contact shadow under the rings (L* 76 against the
  // photo's 56–60). Now the mirror is fully frosted (blur 1) and fainter (0.25), tinted a cool grey
  // (#d0d4e4) so the gold ghost reads greyer, and the bake is tighter (softness 0.35, falloff 1.8: a
  // dense core at each contact, a light tail) drawn at 0.7 — contacts L* 59 / 60, the floor a ring's
  // width away still ≥ 95, gradient p99 1.4, b* +2.3.
  // Critic round 3: still a clear mirror image of both rings with a glint on the stone ring's ghost
  // (floor under the rings L* p1 74 / std 5.1–5.7 against the photo's 87–90 / 2.6–2.7, edge gradient
  // p99 1.4 against 0.8). The mirror is frosted past the old maximum (v68 blur 2: four more blur
  // passes — the ghost keeps the rings' outline but loses its edges and glints) and cut to 0.12,
  // tinted a warm mauve grey (#e6dcdc; the photo's ghost is a* +0.8, ours had a cool cast). The bake
  // is a little softer than round 2's (softness 0.5, falloff 1.8, blur 3) at 0.7: a dense core where
  // each ring stands and a light floor round it. Measured: under the rings p1 85.6 / 85.4 (photo
  // 87 / 90), std 2.8 / 3.3 (2.6 / 2.7), gradient p99 0.79 (0.84), contacts L* 67 / 72 (L9 ≤ 80).
  // The grade is kept off the floor (`lutGround`, writeLook).
  ground: { reflection: true, opacity: 0.12, blur: 2.0, shadowOpacity: 0.7, tint: "#e6dcdc",
    bake: { softness: 0.5, falloff: 1.8, sampleCount: 400, blurRadius: 3 } },
  background: "#fefefe",
};

export const RECIPE_LOOKS: Readonly<Record<RecipeLookId, RecipeLook>> = {
  studio: STUDIO_FALLBACK,
  "catalogue-white": CATALOGUE_WHITE,
};

/** The LUT slot a look's grade is loaded into. */
export const LOOK_LUT_SLOT = 0;

/** The page's one viewer: which look it holds, the studio it held before, and whether a look put a LUT in `LOOK_LUT_SLOT`. */
const lookState: { current: RecipeLookId; studio: RecipeLook | null; lutLoaded: boolean } = { current: "studio", studio: null, lutLoaded: false };

/** For the checks: a fresh page. */
export function resetLookState() {
  lookState.current = "studio";
  lookState.studio = null;
  lookState.lutLoaded = false;
}
export const currentLook = (): RecipeLookId => lookState.current;

/** Read the fields a look writes, off the live viewer (its own getters). */
export async function captureStudio(r: RingBuilderClientApi): Promise<RecipeLook> {
  const [scene, post, groundCfg, bake] = await Promise.all([r.getSceneState(), r.getPostFX(), r.getGroundConfig(), r.getGroundShadowBake()]);
  return {
    env: {
      ...(scene.envPresetId && scene.envPresetId !== "custom" ? { presetId: scene.envPresetId } : { url: scene.envUrl ?? undefined, presetId: scene.envUrl ? undefined : "bandStudio" }),
      intensity: scene.envIntensity,
      rotationY: scene.envRotation?.y ?? 0,
    },
    post: {
      tonemap: post.tonemap,
      contrast: post.grade.contrast,
      saturation: post.grade.saturation,
      ssaoIntensity: post.ssao.intensity,
      tonemapBackground: post.tonemapBackground,
      tonemapGround: post.tonemapGround,
      ...(post.lut ? { lutFlags: { enabled: !!post.lut.enabled, intensity: post.lut.intensity ?? 1, lutBackground: post.lut.lutBackground ?? true, lutGround: post.lut.lutGround ?? true } } : {}),
    },
    ground: {
      reflection: scene.groundReflection,
      opacity: scene.groundReflectionOpacity,
      blur: scene.groundReflectionBlur,
      shadowOpacity: scene.depthShadowOpacity,
      tint: groundCfg?.material?.color ?? STUDIO_FALLBACK.ground.tint,
      bake: bake ? { softness: bake.softness, falloff: bake.falloff, sampleCount: bake.sampleCount, blurRadius: bake.blurRadius } : STUDIO_FALLBACK.ground.bake,
    },
    background: scene.background ?? "#ffffff",
  };
}

/** The RPCs a look is applied with, in their fixed order (the checks pin it). */
export const LOOK_RPC_ORDER = [
  "loadEnvFromUrl|setEnvironment", "setEnvIntensity", "setEnvRotation", "loadLutUrl|clearLut", "setPostFX",
  "setGroundReflection", "setGroundReflectionOpacity", "setGroundReflectionBlur", "setGroundConfig", "setGroundShadowBake", "setDepthShadowOpacity", "setBackground",
] as const;

async function writeLook(r: RingBuilderClientApi, L: RecipeLook) {
  if (L.env.url) await r.loadEnvFromUrl(L.env.url);
  else await r.setEnvironment(L.env.presetId ?? "bandStudio");
  await r.setEnvIntensity(L.env.intensity);
  await r.setEnvRotation(0, L.env.rotationY, 0);
  if (L.post.lut) {
    await r.loadLutUrl(LOOK_LUT_SLOT, L.post.lut.url);
    lookState.lutLoaded = true;
  } else if (lookState.lutLoaded) {
    await r.clearLut(LOOK_LUT_SLOT);
    lookState.lutLoaded = false;
  }
  const post: PostFXSettingsPatch = {
    tonemap: L.post.tonemap,
    grade: { contrast: L.post.contrast, saturation: L.post.saturation },
    ssao: { intensity: L.post.ssaoIntensity },
    tonemapBackground: L.post.tonemapBackground,
    tonemapGround: L.post.tonemapGround,
    // The look's grade on; else the studio's own switch (or off, when nothing was captured).
    // The grade stays off the background and (critic round 3) the floor: its L* shoulder would grey a
    // white floor out to the plane's far edge, a visible horizon behind the rings.
    lut: L.post.lut ? { enabled: true, intensity: L.post.lut.intensity, lutBackground: false, lutGround: false } : (L.post.lutFlags ?? { enabled: false, lutGround: true }),
  };
  await r.setPostFX(post);
  await r.setGroundReflection(L.ground.reflection);
  await r.setGroundReflectionOpacity(L.ground.opacity);
  await r.setGroundReflectionBlur(L.ground.blur);
  // The mirror tint is the ground MATERIAL colour; `setGroundConfig` also re-derives the bake's sample
  // count and blur from the ground config, so the bake (with those two) is written right after it.
  if (L.ground.tint) await r.setGroundConfig({ material: { color: L.ground.tint } });
  if (L.ground.bake) await r.setGroundShadowBake(L.ground.bake);
  await r.setDepthShadowOpacity(L.ground.shadowOpacity);
  await r.setBackground(L.background);
}

/**
 * Put look `id` on the viewer. A no-op when it already holds it (so a recipe
 * without a look, applied over the studio, sends nothing and renders exactly
 * as before v68). Leaving the studio first captures it, so coming back is exact.
 */
export async function applyRecipeLook(r: RingBuilderClientApi, id: RecipeLookId): Promise<void> {
  if (id === lookState.current) return;
  if (lookState.current === "studio" && !lookState.studio) lookState.studio = await captureStudio(r);
  const L = id === "studio" ? (lookState.studio ?? STUDIO_FALLBACK) : RECIPE_LOOKS[id];
  await writeLook(r, L);
  lookState.current = id;
}
